"use client";

import React, { useState, useEffect } from "react";

import { useProfileData } from "../hooks/useProfileData";
import { useReferralLink } from "../hooks/useReferralLink";
import { useWhoAmI } from "../hooks/useWhoAmI";
import { formatNameWithItalicLast } from "../lib/nameUtils";
import "./profile.css";
import { Mail, Linkedin, Twitter } from "lucide-react";
import OctagonAvatar from "../components/octagonal-avatar";
import { AppLoadingScreen } from "../components/app-loading-screen";

export default function ProfilePageClient() {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    gourmetGarden: false,
    gourmetGarden2: false,
  });
  const [logoErrors, setLogoErrors] = useState<Record<number, boolean>>({});
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [_refreshToken, setRefreshToken] = useState<string | null>(null);


  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const getTokens = () => {
      const token = localStorage.getItem("accessToken");
      const refresh = localStorage.getItem("refreshToken");
      setAccessToken(token);
      setRefreshToken(refresh);
    };

    getTokens();

    const handleTokenStored = () => {
      getTokens();
    };
    window.addEventListener("tokenStored", handleTokenStored);
    return () => {
      window.removeEventListener("tokenStored", handleTokenStored);
    };
  }, []);

  const {
    profileData: profileJsonData,
    loading,
    error,
  } = useProfileData(accessToken);

  const { loading: whoamiLoading } = useWhoAmI();
  const { referralLink, loading: referralLoading } = useReferralLink();

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const handleCopyReferralLink = async () => {
    if (referralLink) {
      try {
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current);
        }

        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        copyTimeoutRef.current = setTimeout(() => {
          setCopied(false);
          copyTimeoutRef.current = null;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy referral link:", err);
      }
    }
  };

  const getReferralCode = () => {
    if (!referralLink) return "";

    try {
      const url = new URL(referralLink);
      return url.searchParams.get("referred_by") || "";
    } catch {
      const queryString = referralLink.includes("?") ? referralLink.split("?")[1] : referralLink;
      const params = new URLSearchParams(queryString);
      return params.get("referred_by") || "";
    }
  };

  if (whoamiLoading) {
    return <AppLoadingScreen />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-slate-600">Loading profile data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">Error loading profile data</p>
          <p className="text-slate-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <header className="z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-[24px] pb-2 sm:pb-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <img src={"/images/profound-logo.svg"} alt="Profound Logo" className="h-5 sm:h-6" />
            <img src={"/images/profound-word.svg"} alt="Profound" className="h-4 sm:h-5" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <div className="p-4 sm:p-6">
          <h2 className="mb-4 sm:mb-[24px]">
            <span className="your-profile-your">Your</span>{" "}
            <span className="your-profile-profile">Profile</span>
          </h2>
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
            <div className="relative flex-shrink-0" style={{ width: "80px", height: "80px" }}>
              <div className="w-full h-full scale-[0.83] md:scale-100 origin-top-left">
                <OctagonAvatar
                  size={96}
                  imageSource={profileJsonData?.rawData?.profile_header?.profile_photo_url}
                  borderColor="#10333B"
                  borderWidth={0}
                  n={8}
                  angle={0}
                />
              </div>
              <div className="absolute bottom-1 -right-1 md:-bottom-3 md:-right-4 z-10">
                <img
                  src="/images/profile-sticker.svg"
                  alt="Profile Sticker"
                  className="w-6 h-6 md:w-8 md:h-8"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1
                className="text-2xl sm:text-3xl md:text-[40px]"
                style={{
                  color: "#10333B",
                  fontFamily: "Inter",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "110%",
                  letterSpacing: "-1.6px",
                }}
              >
                {formatNameWithItalicLast(profileJsonData?.rawData?.profile_header?.user_full_name)}
              </h1>

              <div className="space-y-1 mt-2">
                <p className="flex items-center gap-2 profile-info-text">
                  {profileJsonData?.rawData?.profile_header?.location}
                </p>
                <p className="profile-info-text">
                  {profileJsonData?.rawData?.profile_header?.current_role} @{" "}
                  {profileJsonData?.rawData?.profile_header?.current_company}
                </p>
              </div>
            </div>

            {referralLink && (
              <div className="w-full md:w-auto md:flex-shrink-0">
                <div
                  className="p-3 sm:p-4 bg-white rounded-2xl"
                  style={{
                    boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.04), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <div className="flex items-center justify-between md:justify-start gap-2 md:gap-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      Referral Code
                    </span>
                    <button
                      onClick={handleCopyReferralLink}
                      disabled={referralLoading || !referralLink}
                      className="relative w-max px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-2 cursor-pointer hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Copy referral link"
                      title="Copy referral link"
                    >
                      <p className="text-xs sm:text-sm font-mono font-semibold text-[#10333B] whitespace-nowrap">
                        {getReferralCode()}
                      </p>
                      {copied ? (
                        <span className="material-icons text-sm sm:text-base text-[#10333B]">
                          check
                        </span>
                      ) : (
                        <span className="material-icons text-sm sm:text-base text-[#10333B]">
                          link
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white rounded-3xl"
            style={{
              borderRadius: "24px",
              background: "#FFF",
              boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.04), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
            }}
          >
            <h3 className="mb-2 sm:mb-3 ideal-connections-title">
              {profileJsonData?.rawData?.headline_section?.headline}
            </h3>
            <p className="ideal-connections-description ">
              {profileJsonData?.rawData?.headline_section?.tagline}
            </p>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div
              className="p-3 sm:p-4 border border-gray-100"
              style={{
                borderRadius: "24px",
                background: "#FFF",
                boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.04), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
              }}
            >
              <h3 className="mb-4 flex items-center gap-2 text-core-expertise">
                <img src="/images/idea-bulb.svg" alt="Core Expertise" className="w-5 h-5" />
                Core Expertise
              </h3>
              <div className="flex flex-wrap gap-2">
                {profileJsonData?.rawData?.expertise_section?.core_expertise?.map(
                  (item: string, index: number) => (
                    <span
                      key={index}
                      className=" text-body-editorial-italic px-3 py-1.5 bg-yellow-50 text-gray-900 rounded-lg text-sm italic"
                    >
                      {item}
                    </span>
                  )
                )}
              </div>
            </div>

            <div
              className="p-3 sm:p-4 border border-gray-100"
              style={{
                borderRadius: "24px",
                background: "#FFF",
                boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.04), 0 8px 16px 0 rgba(0, 0, 0, 0.08)",
              }}
            >
              <h3 className="mb-4 flex items-center gap-2 text-core-expertise">
                <img src="/images/handshake-image.svg" alt="Seeking To Meet" className="w-5 h-5" />
                Seeking To Meet
              </h3>
              <div className="flex flex-wrap gap-2">
                {profileJsonData?.rawData?.expertise_section?.seeking_to_meet?.map(
                  (item: string, index: number) => (
                    <span
                      className=" text-body-editorial-italic px-3 py-1.5 bg-yellow-50 text-gray-900 rounded-lg text-sm italic"
                      key={index}
                    >
                      {item}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <h2 className="text-career-timeline mb-4 sm:mb-6 flex items-center gap-2">
            Career Timeline
          </h2>

          <div className="space-y-6">
            {profileJsonData?.rawData?.career_timeline?.map((item: any, index: number) => {
              const companyLogoUrl = item?.company_logo_url || item?.logo_url || "";
              const companyName = item?.company_name || "";

              const isValidUrl = (url: string): boolean => {
                if (!url || url.trim() === "") return false;
                try {
                  new URL(url);
                  return true;
                } catch {
                  return url.startsWith("/") || url.startsWith("./");
                }
              };

              const hasLogo = isValidUrl(companyLogoUrl) && !logoErrors[index];
              const companyInitials =
                companyName
                  ?.split(" ")
                  .map((word: string) => word[0])
                  .join("")
                  .substring(0, 2)
                  .toUpperCase() || "";

              return (
                <div key={index} className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
                  <div className="relative">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div
                        className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center overflow-hidden shadow-md flex-shrink-0"
                        style={{
                          backgroundColor: hasLogo ? "transparent" : "transparent",
                          background: hasLogo
                            ? "none"
                            : "linear-gradient(to bottom right, #10b981, #059669)",
                        }}
                      >
                        {hasLogo ? (
                          <img
                            src={companyLogoUrl}
                            alt={companyName}
                            width={64}
                            height={64}
                            className="w-full h-full object-cover"
                            onError={() =>
                              setLogoErrors((prev) => ({
                                ...prev,
                                [index]: true,
                              }))
                            }
                          />
                        ) : (
                          <span className="text-white font-bold text-lg">{companyInitials}</span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="company-name-editorial">{item.company_name}</h3>
                            <p className="role-meta-text">
                              {item.role_title} · {item.date_start} - {item.date_end}
                            </p>
                            <p className="text-location-meta">{item.location}</p>
                          </div>
                          <button
                            onClick={() => toggleSection(item.company_name)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                          >
                            {expandedSections[item.company_name] ? (
                              <img src="/images/expand-icon.svg" alt="Expand" className="w-4 h-4" />
                            ) : (
                              <img
                                src="/images/expand-icon.svg"
                                alt="Collapse"
                                className="w-4 h-4"
                              />
                            )}
                          </button>
                        </div>

                        {expandedSections[item.company_name] && (
                          <div className="mt-4 space-y-3">
                            <p className="text-content">{item.overview}</p>

                            {item.sections.map((section: any, idx: number) => (
                              <div key={idx}>
                                <p className="text-section-title mb-2">{section.title}</p>
                                <p className="text-content">{section.content}</p>
                              </div>
                            ))}

                            {item.key_achievements.map((achievement: any, idx: number) => (
                              <div key={idx}>
                                <p className="text-content mb-2">{achievement}</p>
                              </div>
                            ))}

                            {item.key_learnings.map((learning: any, idx: number) => (
                              <div key={idx}>
                                <p className="text-content mb-2">{learning}</p>
                              </div>
                            ))}

                            <div className="flex flex-wrap gap-2 mt-4">
                              {item.relevant_tags &&
                                item.relevant_tags.map((expertise: any, idx: number) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-1 bg-orange-100 rounded text-expertise-tag"
                                  >
                                    {expertise}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <h2 className="text-career-timeline mb-4">Career narrative</h2>
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <div className=" rounded-xl p-4">
              <h3 className="current-role-dweb mb-3">
                {profileJsonData?.rawData?.profile_header?.current_role}
              </h3>
              <p className="career-narrative-text">
                {profileJsonData?.rawData?.career_narrative?.overall_narrative}
              </p>
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          <h2 className="text-career-timeline mb-4 sm:mb-6">Career Strengths</h2>
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {profileJsonData?.rawData?.core_strengths?.map((item: any, index: number) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
                <h3 className="ideal-connections-title mb-3">{item.title}</h3>
                <p className="ideal-connections-description mb-3">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <h2 className="text-career-timeline mb-4 sm:mb-6">Ideal Connections & Collaborations</h2>
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {profileJsonData?.rawData?.ideal_connections?.map((item: any, index: number) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
                <h3 className="ideal-connections-title mb-3">{item.title}</h3>
                <p className="ideal-connections-description mb-3">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Personal Context & Background */}
        <div className="p-4 sm:p-6">
          <h2 className="text-career-timeline mb-4 sm:mb-6">Personal Context & Background</h2>
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="space-y-6">
              {/* Education */}
              <div>
                <h3 className="education-title mb-3 flex items-center gap-2">Education</h3>
                {profileJsonData?.rawData?.personal_context?.education?.map(
                  (item: any, index: number) => {
                    const educationText =
                      typeof item === "string"
                        ? item
                        : [item?.school_name, item?.degree, item?.field, item?.years]
                            .filter(Boolean)
                            .join(", ");

                    return (
                      <div key={index} className="space-y-2">
                        <p className="text-content">{educationText}</p>
                      </div>
                    );
                  }
                )}
              </div>

              <div>
                <h3 className="education-title mb-3 flex items-center gap-2">
                  Awards & Recognition
                </h3>
                {profileJsonData?.rawData?.personal_context?.awards_recognition?.map(
                  (item: any, index: number) => (
                    <div key={index} className="space-y-2">
                      <p className="text-content">{item}</p>
                    </div>
                  )
                )}
              </div>

              <div>
                <h3 className="education-title flex items-center gap-2 mb-3">Publications</h3>
                <div className="space-y-[2px]">
                  {profileJsonData?.rawData?.personal_context?.publications_thought_leadership?.map(
                    (item: any, index: number) => (
                      <p key={index} className="text-content">
                        {item}
                      </p>
                    )
                  )}
                </div>
              </div>

              <div>
                <h3 className="education-title mb-3">Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {profileJsonData?.rawData?.personal_context?.languages?.map(
                    (item: any, index: number) => (
                      <span key={index} className="language-tag px-3 py-1.5">
                        {item}
                      </span>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          <h2 className="text-career-timeline mb-4">Contact</h2>
          <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:contact@example.com"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Email</span>
              </a>

              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Linkedin className="w-4 h-4" />
                <span className="text-sm font-medium">LinkedIn</span>
              </a>

              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Twitter className="w-4 h-4" />
                <span className="text-sm font-medium">Twitter</span>
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-8 sm:mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="text-center text-xs sm:text-sm text-gray-500">
            Profound Connect • October 29, 2025
            <br />
            Our contacts' trust is priority so only opt-in profiles are shown
          </div>
        </div>
      </footer>
    </div>
  );
}
